import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { 
  Search, 
  Filter, 
  MapPin, 
  Clock, 
  DollarSign, 
  Briefcase, 
  Upload, 
  Send, 
  User, 
  Mail, 
  Link, 
  MessageSquare, 
  CheckCircle,
  ChevronLeft,
  X,
  Menu,
  FileText,
  ExternalLink,
  Award
} from 'lucide-react'
import { jobsData, departments, jobTypes, locations } from '../../data/jobsData'

const Slide7Recruitment = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  })

  const [selectedJob, setSelectedJob] = useState(null)
  const [showApplicationModal, setShowApplicationModal] = useState(false)
  const [applicationSubmitted, setApplicationSubmitted] = useState(false)
  const [submittedData, setSubmittedData] = useState(null)
  const [filters, setFilters] = useState({
    department: 'All Departments',
    type: 'All Types',
    location: 'All Locations',
    search: ''
  })

  const [applicationData, setApplicationData] = useState({
    name: '',
    email: '',
    specialty: '',
    portfolio: '',
    pitch: '',
    resume: null
  })

  const [formErrors, setFormErrors] = useState({})
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const filteredJobs = jobsData.filter(job => {
    const matchesDepartment = filters.department === 'All Departments' || job.department === filters.department
    const matchesType = filters.type === 'All Types' || job.type === filters.type
    const matchesLocation = filters.location === 'All Locations' || job.location === filters.location
    const matchesSearch = filters.search === '' || 
      job.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      job.description.toLowerCase().includes(filters.search.toLowerCase())
    
    return matchesDepartment && matchesType && matchesLocation && matchesSearch
  })

  const openApplicationModal = (job) => {
    setSelectedJob(job)
    setShowApplicationModal(true)
    setApplicationSubmitted(false)
    setFormErrors({})
  }

  const closeModal = () => {
    setShowApplicationModal(false)
    setSelectedJob(null)
    setApplicationSubmitted(false)
    setSubmittedData(null)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setApplicationData(prev => ({
      ...prev,
      [name]: value
    }))
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file && file.size > 5 * 1024 * 1024) {
      setFormErrors(prev => ({ ...prev, resume: 'File must be less than 5MB' }))
      return
    }
    
    if (file && !['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)) {
      setFormErrors(prev => ({ ...prev, resume: 'Only PDF, DOC, and DOCX files' }))
      return
    }
    
    setApplicationData(prev => ({
      ...prev,
      resume: file
    }))
    if (formErrors.resume) {
      setFormErrors(prev => ({ ...prev, resume: '' }))
    }
  }

  const validateForm = () => {
    const errors = {}
    
    if (!applicationData.name.trim()) errors.name = 'Name required'
    if (!applicationData.email.trim()) errors.email = 'Email required'
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(applicationData.email)) {
      errors.email = 'Invalid email'
    }
    if (!applicationData.specialty) errors.specialty = 'Specialty required'
    if (!applicationData.portfolio.trim()) errors.portfolio = 'Portfolio URL required'
    else if (!/^https?:\/\/.+/i.test(applicationData.portfolio)) {
      errors.portfolio = 'Enter valid URL'
    }
    if (!applicationData.resume) errors.resume = 'Resume required'
    if (!applicationData.pitch.trim()) errors.pitch = 'Pitch required'
    else if (applicationData.pitch.length > 200) errors.pitch = 'Max 200 characters'
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const onSubmitApplication = (e) => {
    e.preventDefault()
    
    if (!validateForm()) return

    const submissionData = { 
      ...applicationData, 
      jobId: selectedJob.id,
      jobTitle: selectedJob.title,
      timestamp: new Date().toISOString(),
      applicationId: `APP-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`
    }
    
    console.log('Application submitted:', submissionData)
    
    // Store submitted data
    setSubmittedData(submissionData)
    setApplicationSubmitted(true)
    
    // Reset form data
    setApplicationData({
      name: '',
      email: '',
      specialty: '',
      portfolio: '',
      pitch: '',
      resume: null
    })
    
    // Auto close modal after 3 seconds
    setTimeout(() => {
      closeModal()
    }, 3000)
  }

  const resetFilters = () => {
    setFilters({
      department: 'All Departments',
      type: 'All Types',
      location: 'All Locations',
      search: ''
    })
  }

  return (
    <div 
      id="recruitment" 
      ref={ref}
      className="relative min-h-screen w-full bg-black overflow-hidden py-8 md:py-12"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/20 to-black"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12 px-2">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
            <span className="text-cyan-300 text-xs md:text-sm font-medium">CAREERS</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
            <span className="block text-white mb-1 md:mb-2">Join The</span>
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Quavrion Team
            </span>
          </h2>
          
          <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
            Build the future with us. Explore opportunities to work on cutting-edge technology.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl md:rounded-2xl border border-gray-800 p-4 md:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
              {/* Search */}
              <div className="relative sm:col-span-2 lg:col-span-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="w-full pl-9 pr-4 py-2 md:py-3 bg-white/5 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors text-sm md:text-base"
                />
              </div>

              {/* Department Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={filters.department}
                  onChange={(e) => setFilters({...filters, department: e.target.value})}
                  className="w-full pl-9 pr-4 py-2 md:py-3 bg-white/5 border border-gray-800 rounded-lg text-white appearance-none focus:outline-none focus:border-cyan-500 transition-colors text-sm md:text-base"
                >
                  {departments.map(dept => (
                    <option key={dept} value={dept} className="bg-gray-900">{dept}</option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={filters.type}
                  onChange={(e) => setFilters({...filters, type: e.target.value})}
                  className="w-full pl-9 pr-4 py-2 md:py-3 bg-white/5 border border-gray-800 rounded-lg text-white appearance-none focus:outline-none focus:border-cyan-500 transition-colors text-sm md:text-base"
                >
                  {jobTypes.map(type => (
                    <option key={type} value={type} className="bg-gray-900">{type}</option>
                  ))}
                </select>
              </div>

              {/* Location Filter */}
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={filters.location}
                  onChange={(e) => setFilters({...filters, location: e.target.value})}
                  className="w-full pl-9 pr-4 py-2 md:py-3 bg-white/5 border border-gray-800 rounded-lg text-white appearance-none focus:outline-none focus:border-cyan-500 transition-colors text-sm md:text-base"
                >
                  {locations.map(loc => (
                    <option key={loc} value={loc} className="bg-gray-900">{loc}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-sm text-gray-400">
              <span>{filteredJobs.length} jobs found</span>
              <button 
                onClick={resetFilters}
                className="text-cyan-500 hover:text-white transition-colors text-sm"
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>

        {/* Jobs Grid */}
        {filteredJobs.length === 0 ? (
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl md:rounded-2xl border border-gray-800 p-8 md:p-12 text-center">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
              <Search className="w-6 h-6 md:w-8 md:h-8 text-cyan-500" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
              No Jobs Found
            </h3>
            <p className="text-sm md:text-base text-gray-400 mb-6">
              No positions match your criteria. Check back soon.
            </p>
            <button className="px-6 py-2 md:py-3 bg-transparent border border-cyan-500/50 text-cyan-300 rounded-lg font-medium hover:border-cyan-300 transition-colors text-sm md:text-base">
              Join Talent Network
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredJobs.map((job) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.3 }}
                className="cursor-pointer bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl border border-gray-800 hover:border-cyan-500/30 transition-all duration-300 group"
              >
                <div className="p-4 md:p-6">
                  <div className="flex justify-between items-start mb-3 md:mb-4">
                    <div>
                      <h3 className="text-base md:text-lg font-bold text-white mb-1 md:mb-2 line-clamp-1">
                        {job.title}
                      </h3>
                      <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                        <span className="text-xs text-cyan-500 bg-cyan-500/10 px-1.5 py-0.5 md:px-2 md:py-1 rounded">
                          {job.id}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 md:px-2 md:py-1 rounded ${
                          job.status === 'open' 
                            ? 'bg-green-500/20 text-green-300' 
                            : 'bg-red-500/20 text-red-300'
                        }`}>
                          {job.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-cyan-500 flex-shrink-0" />
                  </div>

                  <div className="space-y-2 mb-3 md:mb-4">
                    <div className="flex items-center text-sm text-gray-400">
                      <Briefcase className="w-3 h-3 md:w-4 md:h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{job.department}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-400">
                      <MapPin className="w-3 h-3 md:w-4 md:h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{job.location}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-400">
                      <Clock className="w-3 h-3 md:w-4 md:h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{job.type}</span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-300 mb-4 line-clamp-2">
                    {job.description}
                  </p>

                  <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                      Posted: {job.posted}
                    </div>
                    <button
                      onClick={() => openApplicationModal(job)}
                      disabled={job.status !== 'open'}
                      className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium text-xs md:text-sm transition-all group-hover:scale-105 ${
                        job.status === 'open'
                          ? 'bg-cyan-500 text-gray-900 hover:bg-cyan-500/90'
                          : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {job.status === 'open' ? 'Apply Now' : 'Closed'}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Talent Network CTA */}
        <div className="mt-8 md:mt-12">
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl md:rounded-2xl border border-gray-800 p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
              <div className="text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                  Join Our Talent Network
                </h3>
                <p className="text-sm md:text-base text-gray-300">
                  Be the first to know about new opportunities at Quavrion
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button className="px-4 py-2 md:px-6 md:py-3 bg-transparent border border-cyan-500/50 text-cyan-300 rounded-lg font-medium hover:border-cyan-300 transition-colors text-sm md:text-base">
                  Join Network
                </button>
                <button className="px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity text-sm md:text-base">
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Application Modal */}
      <AnimatePresence>
        {showApplicationModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-900 to-black rounded-xl md:rounded-2xl border border-gray-800 shadow-2xl"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 bg-gradient-to-b from-gray-900 to-gray-900/95 border-b border-gray-800 p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-white">
                      {applicationSubmitted ? 'Application Submitted!' : 'Apply for Position'}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {applicationSubmitted 
                        ? 'Thank you for your application' 
                        : `Applying for: ${selectedJob?.title}`
                      }
                    </p>
                  </div>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 md:p-6">
                {applicationSubmitted ? (
                  /* Success Screen */
                  <div className="text-center py-8 md:py-12">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 md:w-10 md:h-10 text-green-500" />
                    </div>
                    
                    <h4 className="text-2xl md:text-3xl font-bold text-white mb-4">
                      Application Submitted Successfully!
                    </h4>
                    
                    <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-4 md:p-6 mb-6">
                      <h5 className="text-lg font-bold text-white mb-3">Application Details</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
                        <div>
                          <div className="text-xs text-gray-400">Application ID</div>
                          <div className="text-sm font-mono text-cyan-300">{submittedData?.applicationId}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">Position</div>
                          <div className="text-sm text-white">{submittedData?.jobTitle}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">Name</div>
                          <div className="text-sm text-white">{submittedData?.name}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">Email</div>
                          <div className="text-sm text-white">{submittedData?.email}</div>
                        </div>
                        <div className="md:col-span-2">
                          <div className="text-xs text-gray-400">Specialty</div>
                          <div className="text-sm text-white">{submittedData?.specialty}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <p className="text-gray-300">
                        We've received your application for <span className="text-cyan-300 font-bold">{selectedJob?.title}</span>.
                        Our team will review it and get back to you within 5-7 business days.
                      </p>
                      
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                          onClick={closeModal}
                          className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold rounded-lg hover:opacity-90 transition-opacity"
                        >
                          Browse More Jobs
                        </button>
                        <button className="px-6 py-3 bg-transparent border border-gray-700 text-gray-300 rounded-lg hover:border-cyan-500 transition-colors">
                          Download Receipt
                        </button>
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-6">
                        You'll receive a confirmation email shortly. Check your spam folder if you don't see it.
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Application Form */
                  <form onSubmit={onSubmitApplication} className="space-y-4 md:space-y-6">
                    {/* Job Summary */}
                    <div className="bg-white/5 rounded-lg p-4 border border-gray-800">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-white">{selectedJob?.title}</h4>
                        <span className={`text-xs px-2 py-1 rounded ${
                          selectedJob?.status === 'open' 
                            ? 'bg-green-500/20 text-green-300' 
                            : 'bg-red-500/20 text-red-300'
                        }`}>
                          {selectedJob?.status?.toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div className="flex items-center gap-1 text-gray-400">
                          <Briefcase className="w-3 h-3" />
                          <span>{selectedJob?.department}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400">
                          <MapPin className="w-3 h-3" />
                          <span>{selectedJob?.location}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400">
                          <Clock className="w-3 h-3" />
                          <span>{selectedJob?.type}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400">
                          <DollarSign className="w-3 h-3" />
                          <span>{selectedJob?.salary}</span>
                        </div>
                      </div>
                    </div>

                    {/* Form Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">
                          <User className="inline w-4 h-4 mr-2" />
                          Full Name *
                        </label>
                        <input
                          name="name"
                          value={applicationData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
                          placeholder="Enter your name"
                        />
                        {formErrors.name && (
                          <p className="text-red-400 text-xs mt-1">{formErrors.name}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm text-gray-400 mb-2">
                          <Mail className="inline w-4 h-4 mr-2" />
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={applicationData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
                          placeholder="your.email@domain.com"
                        />
                        {formErrors.email && (
                          <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">
                          <Briefcase className="inline w-4 h-4 mr-2" />
                          Specialty *
                        </label>
                        <select
                          name="specialty"
                          value={applicationData.specialty}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-gray-800 rounded-lg text-white appearance-none focus:outline-none focus:border-cyan-500 transition-colors"
                        >
                          <option value="">Select your specialty</option>
                          <option value="dev">Software Development</option>
                          <option value="design">UI/UX Design</option>
                          <option value="ai">AI/ML Engineering</option>
                          <option value="data">Data Science</option>
                          <option value="marketing">Marketing</option>
                          <option value="operations">Operations</option>
                          <option value="product">Product Management</option>
                          <option value="research">Research</option>
                        </select>
                        {formErrors.specialty && (
                          <p className="text-red-400 text-xs mt-1">{formErrors.specialty}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm text-gray-400 mb-2">
                          <Link className="inline w-4 h-4 mr-2" />
                          Portfolio/LinkedIn URL *
                        </label>
                        <input
                          name="portfolio"
                          value={applicationData.portfolio}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
                          placeholder="https://linkedin.com/in/yourprofile"
                        />
                        {formErrors.portfolio && (
                          <p className="text-red-400 text-xs mt-1">{formErrors.portfolio}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        <FileText className="inline w-4 h-4 mr-2" />
                        Resume/CV *
                      </label>
                      <div className="border border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-cyan-500/50 transition-colors cursor-pointer">
                        <input
                          type="file"
                          onChange={handleFileChange}
                          className="hidden"
                          id="resume-upload"
                          accept=".pdf,.doc,.docx"
                        />
                        <label htmlFor="resume-upload" className="cursor-pointer">
                          <Upload className="w-8 h-8 text-cyan-500 mx-auto mb-3" />
                          <p className="text-sm text-gray-400 mb-2">
                            {applicationData.resume 
                              ? `Selected: ${applicationData.resume.name}`
                              : 'Click to upload resume (PDF, DOC, DOCX)'
                            }
                          </p>
                          <p className="text-xs text-gray-500">
                            Max file size: 5MB • Supported formats: PDF, DOC, DOCX
                          </p>
                        </label>
                      </div>
                      {formErrors.resume && (
                        <p className="text-red-400 text-xs mt-1">{formErrors.resume}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        <MessageSquare className="inline w-4 h-4 mr-2" />
                        Why should we hire you? *
                      </label>
                      <textarea
                        name="pitch"
                        value={applicationData.pitch}
                        onChange={handleInputChange}
                        rows="4"
                        className="w-full px-4 py-3 bg-white/5 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
                        placeholder="Tell us why you're the perfect fit for this role and what unique value you can bring to Quavrion..."
                        maxLength={200}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>
                          {formErrors.pitch && (
                            <span className="text-red-400">{formErrors.pitch}</span>
                          )}
                        </span>
                        <span>{applicationData.pitch.length}/200 characters</span>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4 border-t border-gray-800">
                      <button
                        type="submit"
                        className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        Submit Application
                      </button>
                      <p className="text-xs text-gray-500 text-center mt-3">
                        By submitting, you agree to our privacy policy and consent to contact regarding your application.
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Slide7Recruitment